import { useState, useRef, useImperativeHandle, forwardRef } from 'react';

import styled from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { useLanguage } from '../../Context/LanguageContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';

const ReserveOpenCountInput = forwardRef((props, ref) => {
  const { onCommit } = props;
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const theme = isDark ? darkTheme : lightTheme;

  const [dateVal, setDateVal] = useState(0);
  const [units, setUnits] = useState('일');
  const [time, setTime] = useState('');
  const [timeBorder, setTimeBorder] = useState('')

  const InputRef = useRef();
  const TimeInputRef = useRef();
  // 최근 commit 성공한 (값, 단위, 시각) — 실패 시 롤백 기준 //
  const lastCommittedRef = useRef({ value: 0, units: '일', time: '00:00' });
  // input focus 시점 스냅샷 //
  const dateFocusSnapshotRef = useRef(0);
  const timeFocusSnapshotRef = useRef('00:00');

  // 시각 문자열을 백엔드 검증 정규식 ^([01]\d|2[0-3]):([0-5]\d)$ 에 맞도록 HH:mm 으로 정규화 //
  function normalizeTime(rawTime) {
    const normalized = rawTime === '' ? '00:00' : rawTime;
    return normalized.length === 4 ? `0${normalized}` : normalized;
  }

  // 값/시각 변경 후 외부에 통보 //
  function commit(nextDateVal, nextTime) {
    if (!onCommit) return;

    const safeTime = normalizeTime(nextTime);

    // 변경 없음 → skip
    if (nextDateVal === lastCommittedRef.current.value
      && units === lastCommittedRef.current.units
      && safeTime === lastCommittedRef.current.time) return;

    let multiplier = 1;
    let unitCode = 'min';

    if (units === '일') {
      multiplier = 60 * 24;
      unitCode = 'day';
    }
    else if (units === '시간') {
      multiplier = 60;
      unitCode = 'time';
    }

    const prev = lastCommittedRef.current;
    lastCommittedRef.current = { value: nextDateVal, units, time: safeTime };

    onCommit(
      { value: nextDateVal * multiplier, unit: unitCode, time: safeTime },
      () => {
        setDateVal(prev.value);
        setUnits(prev.units);
        setTime(prev.time);
        lastCommittedRef.current = prev;
      }
    );
  }

  useImperativeHandle(ref, () => ({
    setValue: (val) => {
      setDateVal(val);
      lastCommittedRef.current = { ...lastCommittedRef.current, value: val };
    },
    setUnit: (val) => {
      setUnits(val);
      lastCommittedRef.current = { ...lastCommittedRef.current, units: val };
    },
    getValue: () => {
      let multiplier = 1;

      if (units === '일') multiplier = 60 * 24;
      else if (units === '시간') multiplier = 60;
      else if (units === '분') multiplier = 1;

      return (dateVal * multiplier);
    },
    getUnit: () => {
      return units;
    },
    setTime: (val) => {
      const next = (val === undefined || val.length < 5) ? '00:00' : val;
      setTime(next);
      lastCommittedRef.current = { ...lastCommittedRef.current, time: next };
    },
    getTime: () => {
      return time === '' ? '00:00' : time;
    }
  }))

  // 입력값 변경시 — 숫자만 허용 //
  function onChangeInput(e) {
    const regex = /[^0-9]/g;
    const result = e.target.value.replace(regex, "");

    setDateVal(result === '' ? 0 : parseInt(result));
  }

  // 입력 포커스시 — blur 시 변경 판정용 스냅샷 //
  function onFocusInput() {
    dateFocusSnapshotRef.current = dateVal;
  }

  // 입력 포커스 해제시 //
  function onBlurInput() {
    if (dateVal === dateFocusSnapshotRef.current) return;
    commit(dateVal, time);
  }

  // 증가 버튼 클릭시 //
  function onClickUpButton() {
    const next = dateVal + 1;
    setDateVal(next);
    commit(next, time);
  }

  // 감소 버튼 클릭시 //
  function onClickDownButton() {
    if (dateVal > 0) {
      const next = dateVal - 1;
      setDateVal(next);
      commit(next, time);
    }
  }

  // 오픈시간 변경시 //
  function onChangeTime(e) {
    if (e.target.value.length > 5) return;

    const regex = /^[0-9:\-\b]{0,11}$/;

    if (regex.test(e.target.value)) {
      if (e.target.value.length === 2) setTime(e.target.value.replace(/(\d{2})/, '$1:'));
      else if (e.target.value.length === 5) setTime(e.target.value.replace(/(\d{2}):(\d{2})/, '$1:$2'))
      else setTime(e.target.value);
    }
  }

  // 클릭시 데이터 지워주기 //
  function onFocusTime() {
    timeFocusSnapshotRef.current = time;
    setTime('');
    setTimeBorder('1px solid #FF8B48');
  }

  // 오픈시간 포커스 해제시 — 값이 바뀌었으면 저장 //
  function onBlurTime() {
    setTimeBorder('1px solid #DFDFDF');
    if (normalizeTime(time) === normalizeTime(timeFocusSnapshotRef.current)) return;
    commit(dateVal, time);
  }

  // 오픈시간 Backspace 입력시 — 구분자(:) 함께 지우기 //
  function onKeyDownTime(e) {
    if (e.key === 'Backspace') {
      if (time.length === 3 || time.length === 6 || time.length === 9) {
        setTime(time.substring(0, time.length - 1));
      }
    }
  }

  return (
    <TopLevelWrapper>
      <TopWrapper>
        <LeftWrapper>
          <Input
            ref={InputRef}
            placeholder={0}
            value={dateVal}
            onChange={onChangeInput}
            onFocus={onFocusInput}
            onBlur={onBlurInput} />
          <ButtonWrapper>
            <Button onClick={onClickUpButton}><StyledArrow isUp={true} /></Button>
            <Button onClick={onClickDownButton}><StyledArrow isUp={false} /></Button>
          </ButtonWrapper>
        </LeftWrapper>
        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{t('system.day')}</Text>
        <TimeInput
          ref={TimeInputRef}
          placeholder='00:00'
          value={time}
          border={timeBorder}
          onKeyDown={onKeyDownTime}
          onChange={onChangeTime}
          onBlur={onBlurTime}
          onFocus={onFocusTime} />
      </TopWrapper>
      <Text style={{ marginTop: '6px' }} fontFamily='NotoSansKR-Regular' fontSize={12} color='#FF8B48'>{t('system.bookingOpenHint', { days: dateVal, time: time === '' ? '00:00' : time })}</Text>
    </TopLevelWrapper>
  )
})

export default ReserveOpenCountInput;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 8px;

  transition: all 0.3s ease-in-out;
`;

const TopWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 7px;
`;

const LeftWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 4px;
`;

const Input = styled.input`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 73px;
  height: 30px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  padding: 0 8px 0 8px;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  ::placeholder {
    color: ${props => props.theme.line};
  }
`;

const TimeInput = styled.input`
  display: flex;
  flex-direction: row;

  flex-direction: center;
  align-items: center;

  padding: 0 8px 0 8px;

  width: 73px;
  height: 30px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  text-align: center;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  ::placeholder {
    color: ${props => props.theme.line};
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 4px;
`;

const Button = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;

  border: 1px solid ${props => props.theme.buttonFill};
  border-radius: 8px;
  background-color: ${props => props.theme.buttonFill};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const StyledArrow = styled(LeftArrowSvg)`
  width: 9.6px;
  height: 17.7px;

  transform: ${props => props.isUp ? 'rotate(90deg)' : 'rotate(-90deg)'};

  & path {
    fill: ${props => props.theme.subText};
  }
`;

const Text = styled.p`
  font-fmaily: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};
  margin: 0 0 0 0;
`;
